import React from 'react';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import {
  DialogActionButton,
  DialogActions,
  DialogCancelButton,
  DialogHeader,
} from '@/components/common/CustomDialog';
import ConstituentSearchField from '@/components/common/form/ConstituentSearchField';
import DateInput from '@/components/common/form/DateInput';
import NotesInput from '@/components/common/form/NotesInput';
import TextSelectField from '@/components/common/form/TextSelectField';
import ExperienceRating from '@/components/common/interaction/ExperienceRating';
import Loader from '@/components/common/Loader';
import InteractionMethodFieldConfig from '@/constants/interactions/InteractionMethodFieldConfig';
import { useNewInteractionDialog } from '@/lib/hooks/interactions';
import translate from '@/lib/intl/translate';
import FileUploader from '@/modules/FileUploader';

function InteractionDialog({ data, onClose, preSelectedDonorId, onSuccess }) {
  const {
    // common
    campaigns,
    isEditing,
    teamMembers,

    // statuses
    error,
    isLoading,
    showFormContent,

    // form fields and their setters
    attachments,
    setAttachments,
    selectedDonor,
    setSelectedDonor,
    selectedDate,
    setSelectedDate,
    interactionMethod,
    setInteractionMethod,
    narrative,
    setNarrative,
    employeesInvolved,
    setEmployeesInvolved,
    experienceRating,
    setExperienceRating,
    selectedCampaign,
    setSelectedCampaign,

    // form errors
    fieldErrors,
    showFieldErrors,

    // handlers
    handleSave,
  } = useNewInteractionDialog({ data, onClose, onSuccess });

  return (
    <>
      <DialogHeader
        dataTestid="interaction-form"
        headerText={translate(
          isEditing ? 'EDIT_INTERACTION' : 'ADD_NEW_INTERACTION',
        )}
        onClose={onClose}
      />
      <DialogContent dividers>
        {!showFormContent ? (
          <Loader isCentered />
        ) : (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="col-span-full sm:col-span-2">
              <ConstituentSearchField
                id="contact-name-select-field"
                initialConstituentId={data?.constituentId || preSelectedDonorId}
                disabled={isEditing}
                value={selectedDonor}
                setValue={setSelectedDonor}
                label={translate('INTERACTION_CONTACT_NAME')}
                error={showFieldErrors && fieldErrors?.selectedDonor}
                errorHelperText={translate(
                  'INTERACTION_CONTACT_NAME_ERROR_REQUIRED',
                )}
                required
              />
            </div>
            <div className="col-span-full sm:col-span-1">
              <DateInput
                id="interaction-date-picker-inline"
                label={translate('INTERACTION_DATE')}
                onChange={setSelectedDate}
                value={selectedDate}
              />
            </div>
            <div className="col-span-full">
              <TextSelectField
                id="interaction-method-select-field"
                items={InteractionMethodFieldConfig}
                value={interactionMethod}
                setValue={setInteractionMethod}
                label={translate('INTERACTION_METHOD')}
                error={showFieldErrors && fieldErrors?.interactionMethod}
                errorHelperText={translate('INTERACTION_METHOD_ERROR_REQUIRED')}
                required
              />
            </div>
            <div className="col-span-full">
              {/* TODO(jon): Replace this with the CampaignSelect */}
              <TextSelectField
                id="campaign-select-field"
                items={campaigns}
                value={selectedCampaign}
                setValue={setSelectedCampaign}
                label={translate('INTERACTION_CAMPAIGN')}
                error={showFieldErrors && fieldErrors?.campaign}
                errorHelperText={translate(
                  'INTERACTION_CAMPAIGN_ERROR_REQUIRED',
                )}
              />
            </div>
            <div className="col-span-full pb-3">
              <NotesInput
                label={translate('INTERACTION_NARRATIVE')}
                notes={narrative}
                setNotes={setNarrative}
                maxLength={10000}
              />
            </div>
            <div className="col-span-full">
              <TextSelectField
                id="interaction-employees-select-field"
                items={teamMembers}
                multiple
                value={employeesInvolved}
                setValue={setEmployeesInvolved}
                label={translate('INTERACTION_EMPLOYEES_INVOLVED')}
                error={showFieldErrors && fieldErrors?.employeesInvolved}
                errorHelperText={translate(
                  'INTERACTION_EMPLOYEES_INVOLVED_ERROR_REQUIRED',
                )}
              />
            </div>
            <div className="col-span-full">
              <ExperienceRating
                experience={experienceRating}
                error={showFieldErrors && fieldErrors?.experienceRating}
                errorHelperText={translate(
                  'INTERACTION_EXPERIENCE_ERROR_REQUIRED',
                )}
                setExperience={setExperienceRating}
              />
            </div>
            <div className="col-span-full">
              <div className="mb-4 type-h3">{translate('FILES')}</div>
              <FileUploader
                multiple
                onUploadFinished={setAttachments}
                value={attachments}
              />
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        {Boolean(error && error.error) && (
          <div className="px-2">
            <Typography color="error">{translate('GENERIC_ERROR')}</Typography>
          </div>
        )}
        <DialogActionButton
          data-testid="new-interaction-modal-save-button"
          onClick={() => {
            if (isLoading) {
              return;
            }

            handleSave();
          }}
          showLoader={isLoading}
        />
        <DialogCancelButton
          data-testid="new-interaction-modal-cancel-button"
          onClick={onClose}
        />
      </DialogActions>
    </>
  );
}

InteractionDialog.propTypes = {
  data: PropTypes.shape({
    constituentId: PropTypes.string,
  }),
  onClose: PropTypes.func,
  preSelectedDonorId: PropTypes.string,
  onSuccess: PropTypes.func,
};

InteractionDialog.defaultProps = {
  data: null,
  onClose: () => {},
  preSelectedDonorId: null,
  onSuccess: () => {},
};

export default InteractionDialog;
